import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field } from 'redux-form';
import { toastr } from 'react-redux-toastr';

import {
  addOnlineForm,
  showPrivacyNoticeModal,
  hidePrivacyNoticeModal,
  getStorageSetting
} from 'actions';

import {
  SelectInput,
  CheckboxInput
} from 'components/Common/Inputs';
import AutoCompleteInput from 'components/Common/Inputs/AutoCompleteInput';
import HintButton from 'components/Common/Buttons/HintButton';

import OnlineForm from 'components/OnlineForms/OnlineForm';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';

const isRequired = value => !value || !value.trim();

class OnlineForms extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props);

    const clients = this.filterClientList() || [];
    const clientKey = (clients != null && clients.length === 1) ? clients[0].key : '';

    this.state = {
      clients,
      clientId: clientKey,
      legalEntity: null,
      customer: null,
      whdAgents: {
        uswa: false,
        qi: false,
        ffi: false
      },
      formReference: '',
      leChapter3Shown: false,
      leChapter4Shown: false
    };
  }

  get canShowCRSForms() {
    const { legalEntity } = this.state;
    return legalEntity == null || legalEntity.crsParticipating;
  }

  onClientChange = (e, newValue) => this.setState({ clientId: parseInt(newValue, 10), legalEntity: null, customer: null });

  onLegalEntitySelectedItem = (lookupValue) => {
    const { whdAgents, customer } = this.state;
    whdAgents.uswa = lookupValue != null && (lookupValue.chapter4Status !== 6 && lookupValue.chapter4Status !== 7);
    whdAgents.qi = lookupValue != null && lookupValue.chapter3Status !== 25;
    whdAgents.ffi = lookupValue != null && ((lookupValue.chapter4Status === 6 || lookupValue.chapter4Status === 7) || lookupValue.crsParticipating);

    const newCustomer = customer != null && customer.id === -9999 ? customer : null;

    this.setState({ legalEntity: lookupValue, whdAgents, customer: newCustomer });
  }

  onLegalEntityChange = (e, newValue) => {
    this.setState({ legalEntity: null });
  }

  onCustomerSelectedItem = lookupValue => this.setState({ customer: lookupValue });

  onFormReferenceChange = (e, newValue) => this.setState({ formReference: newValue });

  onCreateCustomerChange = (e, newValue) => {
    if (newValue) {
      this.setState({ customer: { id: -9999, value: '' } });
    } else {
      this.setState({ customer: null });
    }
  }

  addOnlineForm = (formType) => {
    const { dispatch } = this.props;
    const { clientId, legalEntity, customer, whdAgents, formReference } = this.state;

    if (!legalEntity) {
      toastr.error('', 'Please provide a valid value for an existing legal entity.');
      return;
    }
    if (!clientId) {
      toastr.error('', 'Please provide a valid value for an existing client.');
      return;
    }
    if (!customer) {
      toastr.error('', 'Please provide a valid value for an existing customer or create a temporary customer.');
      return;
    }
    if (!whdAgents.uswa && !whdAgents.qi && !whdAgents.ffi) {
      toastr.error('', 'Please ensure a correctly configured legal entity has been selected');
      return;
    }

    dispatch(addOnlineForm(formType, clientId, legalEntity, customer, whdAgents, formReference));
  }

  filterClientList = () => {
    const clients = getStorageSetting('clients') || [];
    const selectedClient = getStorageSetting('selectedClient');
    let filteredClientList = clients;

    if (selectedClient != null) {
      filteredClientList = [];
      filteredClientList.push(selectedClient);
    }

    return filteredClientList;
  }

  closePrivacyNoticeModal() {
    const { dispatch } = this.props;
    dispatch(hidePrivacyNoticeModal());
  }

  openPrivacyNoticeModal() {
    const { dispatch } = this.props;
    dispatch(showPrivacyNoticeModal());
  }

  render() {
    const {
      clients,
      clientId,
      legalEntity,
      customer,
      formReference,
      leChapter3Shown,
      leChapter4Shown
    } = this.state;

    return (
      <div className="online-forms">
        <form
          id="onlineForm"
          onSubmit={ values => this.addOnlineForm(values) }
          className="form-horizontal"
        >
          <div className="col-12 outer-box form-selection mb">
            <div className="col-2">
              <div className="form-group">
                <div className="col-5 col-sm-12">
                  <label htmlFor="clientId" className="form-label">Client *</label>
                </div>
                <div className="col-7 col-sm-12">
                  <Field
                    id="client"
                    name="clientId"
                    component={ SelectInput }
                    required
                    hideHints
                    className="form-select"
                    value={ clientId }
                    validate={ isRequired }
                    disabled={ clients === null || clients.length === 1 }
                    onChange={ (e, newValue) => this.onClientChange(e, newValue) }
                  >
                    { clients != null && clients.length > 1 ? <option value="" disabled>Choose a client</option> : null }
                    { clients != null && clients.map(client => client != null && <option key={ client.key } value={ client.key }>{ client.value }</option>) }
                  </Field>
                </div>
              </div>
            </div>
            <div className="col-3 form-group">
              <div className="col-5 col-sm-12">
                <label htmlFor="legalEntity" className="form-label text-right">Legal Entity *</label>
              </div>
              <div className="col-7 col-sm-12">
                <AutoCompleteInput
                  id="legal-entity-id"
                  name="legalEntity"
                  category="legalentity"
                  context={ clientId != null ? { type: 'client', id: clientId, value: '' } : null }
                  placeholder="Enter legal entity name..."
                  required
                  value={ legalEntity || '' }
                  onSelectedItem={ lookupValue => this.onLegalEntitySelectedItem(lookupValue) }
                  onChange={ (e, newVal, prevVal) => { this.onLegalEntityChange(e, newVal); } }
                />
              </div>
              <div className="col-12 col-sm-12 le-property-labels text-right">
                { legalEntity != null && <span>
                  { `Chapter 4: ${ legalEntity.chapter4Status != null ? legalEntity.chapter4Status.toString().padStart(2, '0') : 'NA' }` }
                  <HintButton hintType="hint" active={ leChapter4Shown } onClick={ () => { this.setState({ leChapter4Shown: !leChapter4Shown }); } } />
                  { `Chapter 3:  ${ legalEntity.chapter3Status != null ? legalEntity.chapter3Status.toString().padStart(2, '0') : 'NA' }` }
                  <HintButton hintType="hint" active={ leChapter3Shown } onClick={ () => { this.setState({ leChapter3Shown: !leChapter3Shown }); } } />
                  { `CRS: ${ legalEntity.crsParticipating ? 'Participating' : 'Non-Participating' }` }
                </span> }
                <div className={ `le-property-desc ${ leChapter3Shown || leChapter4Shown ? '' : 'hide' }` }>
                  { leChapter4Shown && <span>{ `Chapter 4: ${ legalEntity.chapter4Status.toString().padStart(2, '0') } - ${ legalEntity.chapter4StatusDescription }` }</span> }
                  { leChapter3Shown && <span>{ `Chapter 3: ${ legalEntity.chapter3Status.toString().padStart(2, '0') } - ${ legalEntity.chapter3StatusDescription }` }</span> }
                </div>
              </div>
            </div>
            <div className="col-3 form-group">
              <div className="col-4 col-sm-12">
                <label htmlFor="customerId" className="form-label text-right">Customer *</label>
              </div>
              <div className="col-8 col-sm-12">
                <AutoCompleteInput
                  id="customer-id"
                  name="customerId"
                  category="customer"
                  context={ legalEntity }
                  placeholder="Enter customer..."
                  required
                  value={ customer != null && customer.id !== -9999 ? customer : null }
                  disabled={ customer != null && customer.id === -9999 }
                  onSelectedItem={ lookupValue => this.onCustomerSelectedItem(lookupValue) }
                />
                <Field
                  id="create-customer"
                  name="createCustomer"
                  component={ CheckboxInput }
                  iValue="true"
                  item="Create New Customer ID"
                  onChange={ (e, newVal, prevVal) => { this.onCreateCustomerChange(e, newVal); } }
                />
              </div>
            </div>
            <div className="col-3">
              <div className="form-group">
                <div className="col-5 col-sm-12">
                  <label htmlFor="formReference" className="form-label">Form Ref.</label>
                </div>
                <div className="col-7 col-sm-12">
                  <Field
                    id="formReference"
                    name="formReference"
                    component="input"
                    className="form-input"
                    value={ formReference }
                    onChange={ (e, newValue) => this.onFormReferenceChange(e, newValue) }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 outer-box">
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="crse" title="CRS-E Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="ucrse_2019" title="Universal CRS-E Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="crscp" title="CRS-CP Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="ucrscp_2019" title="Universal CRS-CP Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="crsi" title="CRS-I Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="w8bene_2021" title="W-8BEN-E Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="w8ben_2021" title="W-8BEN Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="w8imy_2017" title="W-8IMY Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="w9_2018" title="W-9 Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="w8eci_2021" title="W-8ECI Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="w8exp" title="W-8EXP Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="rc519" title="RC-519 Form" /></div>
            <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="rc518" title="RC-518 Form" /></div>
          </div>
        </form>
      </div>
    );
  }
}

const OnlineReduxForm = reduxForm({
  form: 'onlineForm',
})(OnlineForms);

function mapStateToProps(state) {
  const { forms: {
    isAddingOnlineForm
  } } = state;
  return {
    isAddingOnlineForm
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(OnlineReduxForm, 'OnlineForms'));
